import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const services = [
  {
    icon: "ri-movie-2-line",
    title: "海量影视资源",
    desc: "收录电影、电视剧等各类影视作品，覆盖动作、喜剧、剧情、科幻、悬疑、古装等丰富类型。",
  },
  {
    icon: "ri-filter-3-line",
    title: "精准分类筛选",
    desc: "支持按类型、地区、年份多条件组合筛选，并按热度、上映时间、评分灵活排序。",
  },
  {
    icon: "ri-newspaper-line",
    title: "最新影视资讯",
    desc: "提供影视新闻、深度影评与热门预告，帮助你第一时间掌握影视圈动态。",
  },
  {
    icon: "ri-search-line",
    title: "便捷搜索推荐",
    desc: "通过关键词快速检索作品与资讯，并根据类型、导演等属性为你推荐相似佳作。",
  },
];

const contactList = [
  { icon: "ri-mail-line", label: "反馈邮箱", value: "support@sihuvideo.com" },
  { icon: "ri-time-line", label: "服务时间", value: "每日 09:00 - 22:00" },
  { icon: "ri-map-pin-line", label: "服务范围", value: "面向全国影视爱好者提供在线内容浏览服务" },
];

export default function About() {
  usePageMeta({
    title: "四虎视频-关于我们-专业影视内容平台",
    description:
      "了解四虎视频：我们是一个专业影视内容平台，提供丰富的电影、电视剧资源和最新影视资讯，致力于为用户带来清晰高效的影视浏览与搜索体验。",
    keywords: "关于四虎视频,影视平台介绍,影视内容服务,联系我们,四虎视频",
  });

  return (
    <div className="w-full">
      <section className="bg-primary-900">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            关于四虎视频
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-primary-100/85 md:text-base">
            四虎视频是一个面向广大影视爱好者的专业影视内容平台。我们希望用清晰高效的分类、搜索与推荐，帮助每一位用户轻松找到想看的内容，并第一时间了解影视圈的最新动态。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-6 w-1.5 rounded-full bg-primary-500" />
              平台介绍
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              <p>
                四虎视频定位于专业影视内容门户，致力于为用户提供丰富的电影、电视剧和热门影视资讯。我们汇聚多种类型与地区的最新内容，无论是动作大片、治愈剧集，还是深度影评与新片预告，都能在这里找到。
              </p>
              <p>
                我们相信，好的内容值得被更容易地发现。因此平台围绕“快速查找、分类浏览、详情了解、资讯获取、相关推荐”五大核心能力进行设计，让浏览影视内容变得简单而顺畅。
              </p>
              <p>
                当前站点为影视内容展示与浏览平台，所有作品信息、评分与资讯均用于页面展示与演示，帮助用户了解内容结构与浏览体验。
              </p>
            </div>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-6 w-1.5 rounded-full bg-accent-500" />
              我们提供的服务
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {services.map((service) => (
                <div
                  key={service.title}
                  className="rounded-lg border border-background-200/70 bg-background-50 p-5"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className={`${service.icon} text-2xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {service.desc}
                  </p>
                </div>
              ))}
            </div>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-6 w-1.5 rounded-full bg-secondary-500" />
              使用说明与免责声明
            </h2>
            <div className="mt-4 space-y-4 rounded-lg border border-background-200/70 bg-background-100/60 p-5 text-sm leading-relaxed text-foreground-700">
              <p>
                <strong className="text-foreground-900">使用说明：</strong>
                你可以通过顶部导航进入电影、电视剧与资讯专区，使用筛选、排序与搜索功能快速定位感兴趣的内容；在作品详情页可查看简介、剧集目录与相关推荐。
              </p>
              <p>
                <strong className="text-foreground-900">免责声明：</strong>
                本站为影视内容平台演示站点，站内作品信息、评分、剧集及资讯内容均为演示数据，播放器为前端占位演示，不提供真实视频资源，也不涉及任何版权内容的实际播放与下载。若内容涉及侵权，请通过下方联系方式与我们沟通。
              </p>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-4 space-y-4">
                {contactList.map((contact) => (
                  <li key={contact.label} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className={`${contact.icon} text-lg`} />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{contact.label}</p>
                      <p className="text-sm text-foreground-900">{contact.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-secondary-100 p-5">
              <h2 className="font-heading text-lg font-bold text-secondary-900">快速开始</h2>
              <div className="mt-4 flex flex-col gap-3">
                <Link
                  to="/category/movie"
                  className="flex items-center justify-between rounded-md bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-700"
                >
                  浏览电影分类
                  <i className="ri-arrow-right-line text-base" />
                </Link>
                <Link
                  to="/category/tv"
                  className="flex items-center justify-between rounded-md bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-700"
                >
                  浏览电视剧分类
                  <i className="ri-arrow-right-line text-base" />
                </Link>
                <Link
                  to="/news"
                  className="flex items-center justify-between rounded-md bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-700"
                >
                  阅读影视资讯
                  <i className="ri-arrow-right-line text-base" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-background-100/60">
        <div className="mx-auto w-full max-w-[900px] px-4 py-10 md:px-6 md:py-14">
          <div className="mb-6 text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              意见反馈
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              如果你在使用过程中遇到问题，或对平台有任何建议，欢迎留下你的反馈，我们会认真查看每一条留言。
            </p>
          </div>
          <FeedbackForm />
        </div>
      </section>
    </div>
  );
}